import React from 'react';
import type {
  AiReport,
  AiReportJobStatus,
  CarePlan,
  CareTodo,
  CareTodoLog,
  CareVisit,
  HerzensappPatient,
  HerzensappStatus,
  VitalSignDef,
  VitalSignEntry,
  VoiceNoteResult,
  Wound,
  WoundAssessment,
} from '../../../services/herzensappApi';
import type { DemoRoute } from '../demoMode';
import type { FeatureTour } from '../tourTypes';

const HerzensappPage = React.lazy(() => import('../../herzensapp/HerzensappPage'));

// Invented demo data only — no real person or agency.
const at = (daysAgo: number, hour: number, minute = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
};
const day = (daysAgo: number) => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const PID = 'demo-huber';
const CAREGIVER = 'Ana Popescu';

const PATIENTS: HerzensappPatient[] = [
  { id: PID, firstName: 'Josefine', lastName: 'Huber', isActive: true, openWounds: 1, lastVisitAt: at(0, 8, 15), activeTodos: 4 },
  { id: 'demo-moser', firstName: 'Franz', lastName: 'Moser', isActive: true, openWounds: 0, lastVisitAt: at(0, 9, 30), activeTodos: 2 },
  { id: 'demo-weber', firstName: 'Hildegard', lastName: 'Weber', isActive: true, openWounds: 0, lastVisitAt: at(1, 18, 0), activeTodos: 3 },
];

const visit = (id: string, daysAgo: number, notes: Array<[number, number, string, string]>): CareVisit => ({
  id,
  patientId: PID,
  nurseUserId: null,
  nurseName: CAREGIVER,
  startedAt: at(daysAgo, 7, 30),
  endedAt: at(daysAgo, 20, 0),
  notes: notes.map(([h, m, category, text], i) => ({ id: `${id}-n${i}`, time: at(daysAgo, h, m), category, text })),
  createdAt: at(daysAgo, 20, 0),
});

const VISITS: CareVisit[] = [
  visit('v1', 0, [
    [7, 45, 'koerperpflege', 'Ganzkörperwäsche am Waschbecken, Frau Huber hat gut mitgeholfen. Haut intakt, eingecremt.'],
    [8, 15, 'ernaehrung', 'Frühstück vollständig gegessen, 2 Tassen Tee getrunken.'],
    [8, 30, 'medikamente', 'Morgenmedikation laut Plan verabreicht.'],
    [10, 0, 'mobilisation', 'Spaziergang im Garten, 20 Minuten mit Rollator. Gute Stimmung.'],
  ]),
  visit('v2', 1, [
    [8, 0, 'koerperpflege', 'Duschen mit Unterstützung, Haare gewaschen.'],
    [12, 30, 'ernaehrung', 'Mittagessen zur Hälfte gegessen, wenig Appetit.'],
    [15, 0, 'beobachtung', 'Frau Huber war nachmittags etwas müde, hat 1 Stunde geschlafen.'],
  ]),
  visit('v3', 2, [
    [8, 10, 'medikamente', 'Morgenmedikation verabreicht.'],
    [16, 0, 'prophylaxen', 'Lagerung zur Druckentlastung, Ferse rechts kontrolliert.'],
  ]),
];

const entry = (defId: string, i: number, value: string, valueNum: string | null, daysAgo: number, note: string | null = null): VitalSignEntry => ({
  id: `${defId}-e${i}`,
  defId,
  value,
  valueNum,
  measuredAt: at(daysAgo, 8, 0),
  note,
  recordedByName: CAREGIVER,
});

const BP = ['128/80', '132/82', '126/78', '135/84', '130/80', '138/85', '134/82', '129/79', '136/84', '133/81', '152/88'];
const PULSE = [72, 75, 70, 74, 78, 73, 76, 71, 74, 77, 84];
const WEIGHT = [64.2, 64.0, 63.9, 64.1, 63.8];

const VITALS: VitalSignDef[] = [
  {
    id: 'vd-bp', name: 'Blutdruck', unit: 'mmHg', description: 'Systolischer Wert maßgeblich', schedule: null,
    targetMin: '100', targetMax: '140', isActive: true,
    entries: BP.map((v, i) => entry('vd-bp', i, v, v.split('/')[0], (BP.length - 1 - i) * 2,
      i === BP.length - 1 ? 'Leichte Kopfschmerzen, Büro informiert' : null)).reverse(),
  },
  {
    id: 'vd-pulse', name: 'Puls', unit: '/min', description: null, schedule: null,
    targetMin: '60', targetMax: '100', isActive: true,
    entries: PULSE.map((v, i) => entry('vd-pulse', i, String(v), String(v), (PULSE.length - 1 - i) * 2)).reverse(),
  },
  {
    id: 'vd-weight', name: 'Gewicht', unit: 'kg', description: null, schedule: null,
    targetMin: '58', targetMax: '72', isActive: true,
    entries: WEIGHT.map((v, i) => entry('vd-weight', i, String(v), String(v), (WEIGHT.length - 1 - i) * 7)).reverse(),
  },
];

const log = (todoId: string, i: number, daysAgo: number, timeOfDay: string | null, status: CareTodoLog['status'] = 'done', message: string | null = null): CareTodoLog => ({
  id: `${todoId}-l${i}`,
  todoId,
  date: day(daysAgo),
  timeOfDay,
  status,
  message,
  doneByName: CAREGIVER,
  doneAt: at(daysAgo, timeOfDay === 'evening' ? 19 : timeOfDay === 'noon' ? 12 : 8, 30),
});

const todo = (id: string, title: string, description: string | null, schedule: Record<string, any>, logs: CareTodoLog[], abedlCategory: string | null = null): CareTodo => ({
  id, title, description, schedule, isActive: true,
  diagnosisId: null, goalId: null, delegationId: null, abedlCategory, todoType: 'intervention', logs, openSlots: [],
});

const baseTodos = (): CareTodo[] => [
  todo('t1', 'Medikamente geben', 'Laut Medikationsplan, mit einem Glas Wasser.', { type: 'daily', timesOfDay: ['morning', 'noon', 'evening'], priority: 'high' },
    [log('t1', 0, 0, 'morning'), log('t1', 1, 1, 'morning'), log('t1', 2, 1, 'noon'), log('t1', 3, 1, 'evening')]),
  todo('t2', 'Blutdruck messen', 'Morgens vor dem Frühstück, sitzend.', { type: 'daily', timesOfDay: ['morning'] },
    [log('t2', 0, 0, 'morning', 'done', '152/88 mmHg – Büro informiert'), log('t2', 1, 1, 'morning')]),
  todo('t3', 'Verbandswechsel Ferse rechts', 'Hydrokolloidverband, Wundrand beobachten.', { type: 'daily', timesOfDay: ['morning'] },
    [log('t3', 0, 0, 'morning'), log('t3', 1, 2, 'morning')]),
  todo('t4', 'Trinkmenge kontrollieren', 'Ziel: mindestens 1,5 Liter am Tag.', { type: 'daily', timesOfDay: ['evening'] },
    [log('t4', 0, 1, 'evening')]),
];

// Logs ticked during the tour; reset whenever the page remounts (status call).
let extraLogs: CareTodoLog[] = [];
const todos = () => baseTodos().map((t) => ({ ...t, logs: [...extraLogs.filter((l) => l.todoId === t.id), ...t.logs] }));

const EMPTY_PLAN: CarePlan = { diagnoses: [], delegations: [], unlinkedTodos: [] };

const ASSESSMENT: WoundAssessment = {
  id: 'wa1', woundId: 'w1', assessedAt: at(1, 9, 0), assessedByName: 'Maria H. (DGKP)',
  lengthMm: '18', widthMm: '12', depthMm: '2', undermining: null,
  tissueGranulationPct: 70, tissueFibrinPct: 30, tissueNecrosisPct: 0, tissueEpithelPct: null,
  exudateAmount: 'wenig', exudateType: 'seroes', odor: 'kein',
  woundEdge: ['reizlos'], surroundingSkin: ['intakt'], infectionSigns: [],
  painLevel: 2, painNotes: null, cleansing: 'NaCl 0,9 %', dressing: 'Hydrokolloidverband',
  treatmentNotes: 'Ferse weiterhin konsequent freilagern.', nextChangeDate: day(-2), notes: null,
};
const OLDER_ASSESSMENT: WoundAssessment = {
  ...ASSESSMENT, id: 'wa0', assessedAt: at(8, 9, 0), lengthMm: '24', widthMm: '16', depthMm: '3',
  tissueGranulationPct: 50, tissueFibrinPct: 50, painLevel: 4, treatmentNotes: null,
};

const WOUND: Wound = {
  id: 'w1', patientId: PID, woundNumber: 1, title: 'Dekubitus Ferse', woundType: 'dekubitus',
  localization: 'Ferse', bodyMapX: '0.49', bodyMapY: '0.955', bodySide: 'rechts', classification: 'Kategorie 2',
  originDate: day(14), anamnesis: 'Entstanden durch längere Bettlägerigkeit nach Infekt.',
  status: 'in_abheilung', healedAt: null, createdByName: 'Maria H. (DGKP)', createdAt: at(14, 10, 0),
  latestAssessment: ASSESSMENT, photoCount: 0,
};

// KI-Entwürfe, die während der Vorschau "erstellt" werden.
let aiReports: AiReport[] = [];
const DEMO_AI_TEXT =
  'Pflegebericht (Entwurf)\n\nFrau Huber war im Berichtszeitraum überwiegend gut gelaunt und hat bei der Körperpflege aktiv mitgeholfen. ' +
  'Die Medikation wurde laut Plan verabreicht. Der Blutdruck lag meist im Zielbereich, zuletzt einmalig erhöht (152/88 mmHg), das Büro wurde informiert. ' +
  'Die Wunde an der Ferse rechts zeigt eine deutliche Verkleinerung (24 × 16 mm → 18 × 12 mm) mit zunehmendem Granulationsgewebe.';

const ok = (data: unknown) => ({ success: true, data });
const status: HerzensappStatus = { active: true, role: 'AGENCY_ADMIN', patientId: null };

const mocks: DemoRoute[] = [
  { path: /^\/herzensapp\/status$/, reply: () => { extraLogs = []; aiReports = []; return ok(status); } },
  { path: /^\/herzensapp\/patients$/, reply: () => ok(PATIENTS) },
  { path: /^\/herzensapp\/patients\/[^/]+\/visits$/, reply: () => ok(VISITS) },
  { path: /^\/herzensapp\/patients\/[^/]+\/voice-notes$/, reply: () => ok([]) },
  { path: /^\/herzensapp\/speech-languages$/, reply: () => ok([{ code: 'de-AT', label: 'Deutsch' }, { code: 'ro-RO', label: 'Română' }]) },
  { path: /^\/herzensapp\/patients\/[^/]+\/vitals$/, reply: () => ok(VITALS) },
  { path: /^\/herzensapp\/patients\/[^/]+\/todos$/, reply: () => ok(todos()) },
  { path: /^\/herzensapp\/patients\/[^/]+\/care-plan$/, reply: () => ok(EMPTY_PLAN) },
  { path: /^\/herzensapp\/todo-templates$/, reply: () => ok([]) },
  { path: /^\/herzensapp\/patients\/[^/]+\/wounds$/, reply: () => ok([WOUND]) },
  { path: /^\/herzensapp\/wounds\/[^/]+$/, reply: () => ok({ ...WOUND, assessments: [ASSESSMENT, OLDER_ASSESSMENT], photos: [] }) },
  { path: /^\/herzensapp\/localizations$/, reply: () => ok([]) },
  { path: /^\/herzensapp\/patients\/[^/]+\/document-images$/, reply: () => ok([]) },
  { path: /^\/herzensapp\/patients\/[^/]+\/ai-reports$/, reply: () => ok(aiReports) },
  { path: /^\/herzensapp\/patients\/[^/]+\/ai-report-jobs\/active$/, reply: () => ok(null) },
  {
    path: /^\/herzensapp\/patients\/[^/]+\/care-report$/,
    reply: ({ params }) => ok({
      patient: { id: PID, firstName: 'Josefine', lastName: 'Huber', birthDate: '1942-05-12' },
      period: { from: params.from, to: params.to },
      generatedAt: new Date().toISOString(),
      diagnoses: [], unlinkedTodos: [], delegations: [], visits: [], vitals: [], wounds: [],
    }),
  },
  { path: /^\/herzensapp\/app-users$/, reply: () => ok([]) },
  {
    path: /^\/herzensapp\/nurses$/,
    reply: () => ok({
      moduleActive: true,
      patients: PATIENTS.map(({ id, firstName, lastName }) => ({ id, firstName, lastName })),
      availableCaregivers: [],
      nurses: [],
    }),
  },
  // Writes whose response the page reads — the generic `data: null` would
  // crash or show an error toast.
  {
    method: 'post',
    path: /^\/herzensapp\/patients\/([^/]+)\/ai-report\/jobs$/,
    delayMs: 1500,
    reply: ({ match, body }) => {
      const report: AiReport = {
        id: `demo-ai-${aiReports.length + 1}`, kind: 'pflegebericht', woundId: null,
        periodFrom: body?.from || null, periodTo: body?.to || null, text: DEMO_AI_TEXT,
        provider: 'demo', model: 'demo', inputTokens: 0, outputTokens: 0,
        createdByName: 'Demo', createdAt: new Date().toISOString(),
      };
      aiReports = [report, ...aiReports];
      const job: AiReportJobStatus = {
        jobId: `demo-job-${aiReports.length}`, stage: 'done', progress: 100, patientId: match[1],
        reportId: report.id, text: DEMO_AI_TEXT,
      };
      return ok(job);
    },
  },
  {
    method: 'post',
    path: /^\/herzensapp\/wounds\/[^/]+\/ai-analysis$/,
    delayMs: 1500,
    reply: () => ok({
      id: null,
      text: 'Wundanalyse (Entwurf)\n\nDie Wunde hat sich seit der Vorwoche verkleinert (24 × 16 mm → 18 × 12 mm). ' +
        'Der Granulationsanteil ist auf 70 % gestiegen, Fibrin rückläufig, keine Infektionszeichen. Empfehlung: Therapie fortführen, Ferse weiterhin freilagern.',
      usage: { inputTokens: 0, outputTokens: 0 },
    }),
  },
  {
    method: 'post',
    path: /^\/herzensapp\/patients\/[^/]+\/voice-notes$/,
    delayMs: 800,
    reply: ({ body }) => {
      const text = 'Frau Huber hat heute gut gegessen und war beim Spaziergang gut gelaunt.';
      const note: VoiceNoteResult = {
        id: 'demo-voice-1', language: 'de', transcript: text, translatedText: null, text,
        durationSeconds: Number(body?.get?.('durationSeconds')) || 5, createdAt: new Date().toISOString(),
      };
      return ok(note);
    },
  },
  { method: 'post', path: /^\/herzensapp\/localizations$/, reply: ({ body }) => ok(body?.name ? [body.name] : []) },
  { method: 'post', path: /^\/herzensapp\/patients\/[^/]+\/wounds$/, reply: () => ok(WOUND) },
  { method: 'post', path: /^\/herzensapp\/patients\/[^/]+\/todos\/from-template$/, reply: ({ body }) => ok({ created: body?.itemIndexes?.length || 0 }) },
  {
    method: 'post',
    path: /^\/herzensapp\/todos\/([^/]+)\/logs$/,
    delayMs: 400,
    reply: ({ match, body }) => {
      const created: CareTodoLog = {
        id: `demo-log-${extraLogs.length}`, todoId: match[1], date: body?.date || day(0),
        timeOfDay: body?.timeOfDay ?? null, status: body?.status || 'done', message: body?.message || null,
        doneByName: CAREGIVER, doneAt: new Date().toISOString(),
      };
      extraLogs = [created, ...extraLogs];
      return ok(created);
    },
  },
];

const k = (key: string) => `featurePreview.pflegedoku.${key}`;

export const pflegedokuTour: FeatureTour = {
  Page: HerzensappPage,
  mocks,
  chapters: [
    {
      title: k('ch1'),
      steps: [
        { at: 0.5, target: 'pd-patient-row', balloon: { title: k('tourListTitle'), text: k('tourListText') } },
        { at: 4, target: 'pd-patient-row', action: 'click' },
        { at: 5, target: 'pd-tab-visits', action: 'click' },
        { at: 6, target: 'pd-visits', balloon: { title: k('hs1Title'), text: k('hs1Text') } },
      ],
      duration: 12,
    },
    {
      title: k('ch2'),
      steps: [
        { at: 0.3, target: 'pd-patient-row', action: 'click' },
        { at: 1.2, target: 'pd-tab-vitals', action: 'click' },
        { at: 2, target: 'pd-vitals', balloon: { title: k('hs2Title'), text: k('hs2Text') } },
      ],
      duration: 10,
    },
    {
      title: k('ch3'),
      steps: [
        { at: 0.3, target: 'pd-patient-row', action: 'click' },
        { at: 1.5, target: 'pd-todos', balloon: { title: k('tourTodosTitle'), text: k('tourTodosText') } },
        { at: 5.5, target: 'pd-todo-done', action: 'click' },
        { at: 7.5, target: 'pd-tab-wounds', action: 'click' },
        { at: 8.5, target: 'pd-wounds', balloon: { title: k('hs3Title'), text: k('hs3Text') } },
      ],
      duration: 14,
    },
  ],
  savings: {
    aLabel: k('savA'), aDefault: 6, aMax: 40,
    bLabel: k('savB'), b: 5,
    toHoursPerMonth: (a, b) => (a * b * 30) / 60,
  },
};
